// The pending-plan store — what carries a CONFIRMED multi-step plan across a navigation (the page unmounts
// Otto and a new Otto mounts on the destination). localStorage, like the chat/undo persistence. Safety
// properties, in order of importance:
//   - Written ONLY on explicit confirm (the plan card's Run) — never speculatively.
//   - TTL'd on INACTIVITY (3 minutes since the plan last did something) while it is RUNNING, suspended
//     while it is BLOCKED on a user decision, and always bounded by a hard ceiling on total age: a stale
//     plan must NEVER fire on an unrelated later visit.
//   - At-most-once per step: the cursor is advanced BEFORE a step dispatches (advancePlanCursor), so a
//     reload mid-step loses that step rather than double-applying it.
//   - Consumed on completion, cleared by the chat's Clear button, and any read of an invalid/expired
//     entry self-cleans.
const KEY = "fai.pendingPlan";

// The TTL is measured from the plan's LAST ACTIVITY, not from confirm. It used to run from `confirmedAt`,
// which meant a plan that stopped to ask the user a question was racing the very human it was waiting for:
// a 3-step plan paused on an ambiguous filter, the user spent 2m48s reading Otto's own clarification, the
// entry self-destructed ~1s before they hit Apply, and the export step was dropped (trace 4c9d3029 /
// FTO-7). Deliberation is not staleness. `touchPlan()` restarts the clock whenever the plan hands control
// back to the user, so the window is "3 minutes of silence", which is what the safety property actually
// wants to prevent.
//
// This window governs plans that are RUNNING. A plan that has stopped to ask a question isn't running at
// all, and restarting a 3-minute clock at the moment Otto asks still measures the wrong thing: the next
// report (trace 6d475bbd / FTO-8) took 6m45s to answer a card offering four identically-named options, so
// the touched window missed by 3m44s and its navigate step was dropped exactly as before. While a plan is
// BLOCKED (see blockPlan) the inactivity clock is therefore suspended entirely — Otto has told the user in
// so many words that "the step after this one will continue once you apply", and how long a human takes to
// decide is not something a clock can guess. PLAN_MAX_AGE_MS remains the bound.
export const PLAN_TTL_MS = 3 * 60 * 1000;

// Absolute backstop so refreshing can't keep a plan alive indefinitely — the original guarantee ("a stale
// plan must NEVER fire on an unrelated later visit") has to survive any number of touches, and it is the
// ONLY bound left once a plan is blocked on a user decision.
export const PLAN_MAX_AGE_MS = 30 * 60 * 1000;

const read = () => {
    try {
        const raw = typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem(KEY) : null;
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const write = plan => {
    try {
        window.localStorage.setItem(KEY, JSON.stringify(plan));
    } catch (e) {
        /* quota/blocked — the plan degrades to same-page-only execution */
    }
};

export const clearPlan = () => {
    try {
        window.localStorage.removeItem(KEY);
    } catch (e) {
        /* ignore */
    }
};

// Persist a freshly CONFIRMED plan (cursor 0). `steps` are the validated proposal steps
// [{ capability, sub_utterance, target_page, label }]; planId ties progress lines + traces together.
export const savePlan = ({ planId, steps }) => {
    if (!planId || !Array.isArray(steps) || !steps.length) return null;
    const now = Date.now();
    const plan = { planId, steps, cursor: 0, confirmedAt: now, lastActivityAt: now };
    write(plan);
    return plan;
};

// The current pending plan, or null. Self-cleans expired/exhausted/malformed entries.
export const loadPlan = () => {
    const p = read();
    if (!p || !Array.isArray(p.steps) || typeof p.cursor !== "number" || !p.confirmedAt) {
        if (p) clearPlan();
        return null;
    }
    // Entries written before lastActivityAt existed fall back to confirmedAt — same behavior as before.
    const lastActivityAt = p.lastActivityAt || p.confirmedAt;
    const now = Date.now();
    // A blocked plan is waiting on a decision the user was PROMISED would continue their plan, so silence
    // is expected rather than suspicious: only the absolute age ceiling applies to it.
    const idleTooLong = !p.awaitingUserAt && now - lastActivityAt > PLAN_TTL_MS;
    if (idleTooLong || now - p.confirmedAt > PLAN_MAX_AGE_MS || p.cursor >= p.steps.length) {
        clearPlan();
        return null;
    }
    return p;
};

// Restart the inactivity clock. Call whenever the plan does something and stays in charge.
export const touchPlan = () => {
    const p = loadPlan();
    if (!p) return null;
    const next = { ...p, lastActivityAt: Date.now() };
    write(next);
    return next;
};

// Mark the plan BLOCKED on a user decision (a clarification, a pick) — control has left the plan and it
// cannot proceed until the user acts. Suspends the inactivity expiry (see PLAN_TTL_MS); the plan still dies
// at PLAN_MAX_AGE_MS, and `advancePlanCursor` unblocks it the moment it resumes. Deliberately NOT set for
// work the plan performs itself: an autoRun card that applies without asking stays a running plan.
export const blockPlan = () => {
    const p = loadPlan();
    if (!p) return null;
    const now = Date.now();
    const next = { ...p, lastActivityAt: now, awaitingUserAt: now };
    write(next);
    return next;
};

// Advance past the step about to DISPATCH (call before executing it — at-most-once). Returns the updated
// plan, or null when the store no longer holds a live plan.
export const advancePlanCursor = () => {
    const p = loadPlan();
    if (!p) return null;
    // Dispatching means the plan is running again, so it stops being blocked and the inactivity clock —
    // the right instrument for a plan in charge of itself — applies to this step from now.
    const next = { ...p, cursor: p.cursor + 1, lastActivityAt: Date.now(), awaitingUserAt: null };
    if (next.cursor >= next.steps.length) {
        // The final step is dispatching; nothing left to resume — consume the entry now so a reload
        // can't replay a finished plan.
        clearPlan();
        return next;
    }
    write(next);
    return next;
};
